<template>
  <div ref="root" class="sp-toolbar" :class="classModifiers">
    <div ref="content">
      <slot ref="slot" />
    </div>
  </div>
</template>

<script setup>
import { useWindowScroll } from "@vueuse/core";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { toBoolean } from "../../utils/props";

const props = defineProps({
  /**
   * Whether the toolbar should be sticky or not.
   *
   * @type {Boolean}
   * @default false
   */
  sticky: {
    type: [Boolean, String],
    default: false,
  },

  /**
   * Whether the toolbar should be sticky on scroll or not.
   * Just like the `sticky` prop, but this one will be sticky on scroll.
   * If `sticky` is defined, this prop will be ignored.
   *
   * @type {Boolean}
   * @default false
   */
  stickyOnScroll: {
    type: [Boolean, String],
    default: false,
  },

  /**
   * A selector of sticky content used to calculate the offselt
   *
   * @type {Boolean}
   * @default false
   */
  offsetSelector: {
    type: String,
    default: undefined,
  },

  /**
   * The number of pixels to account for other sticky content on the page
   *
   * @type {Boolean}
   * @default false
   */
  offset: {
    type: [Number, String],
    default: 0,
  },

  /**
   * Whether the toolbar should be positioned absolute or not.
   *
   * @type {Boolean}
   * @default false
   */
  absolute: {
    type: [Boolean, String],
    default: false,
  },

  /**
   * The color of the toolbar.
   *
   * @type {String}
   * @default undefined
   */
  color: {
    type: String,
    default: undefined,
  },

  /**
   * The elevation of the toolbar.
   *
   * @type {String}
   * @default undefined
   */
  elevation: {
    type: String,
    default: undefined,
    validator: (value) => [1, 2, 3].includes(value),
  },
});

const { y } = useWindowScroll();

const content = ref(null);
const slotContents = computed(() => {
  if (content.value?.firstElementChild?.tagName === "SLOT") {
    return content.value.firstElementChild.assignedElements();
  }
  return Array.from(content.value?.children ?? []);
});

const slotContent = computed(() => (slotContents.value ?? []).at(0));

const slotContentOffsetTop = ref(0);
const stickToTop = ref(false);

/**
 * Watch the `stickToTop` ref and update the `slotContent.stuckToTop` property.
 * This will allow the slot content to know if it's stuck to the top or not.
 */
watch(stickToTop, (isSticky) => {
  if (!slotContent.value) {
    return;
  }
  nextTick(() => {
    slotContent.value.stuckToTop = isSticky;
    slotContent.value.classList.toggle("--toolbar-is-sticky", isSticky);
  });
});

const classModifiers = computed(() => ({
  "--is-sticky": toBoolean(props.sticky) || stickToTop.value,
  "--is-absolute": toBoolean(props.absolute),
  [`--elevation-${props.elevation}`]: props.elevation,
  [`--color-${props.color}`]: props.color,
}));

const root = ref(null);

function getScrollPaddingTopProperty() {
  return window.getComputedStyle(document.documentElement).getPropertyValue("--sp-sys-scroll-padding-top-default");
}

function setScrollPaddingTopProperty() {
  const currentPadding = getScrollPaddingTopProperty();
  const rootElHeight = root.value.getBoundingClientRect().height;
  const offsetTop = "2rem";

  document.documentElement.style.setProperty(
    "--sp-sys-scroll-padding-top",
    `calc(${currentPadding} + ${rootElHeight}px + ${offsetTop})`,
  );
}

const unwatch = ref(null);

const offset = computed(() => {
  const parsedOffset = props.offsetSelector
    ? document.querySelector(props.offsetSelector)?.clientHeight
    : Number.parseInt(props.offset);

  return parsedOffset || 0;
});

const offsetInPx = computed(() => `${offset.value}px`);

function initializePositionWatcher() {
  if (toBoolean(props.stickyOnScroll)) {
    setScrollPaddingTopProperty();

    const callback = () => {
      // If the toolbar is not sticky, calculate the offset top.
      // This is necessary to do on demand because the dom might be mutated which might change the offset top.
      if (!stickToTop.value) {
        slotContentOffsetTop.value = calculateOffsetTop();
      }

      stickToTop.value = window.scrollY + offset.value >= slotContentOffsetTop.value;
    };

    unwatch.value = watch(y, () => callback());
    // Call the callback once to initialize the scroll position.
    callback();
  } else {
    unwatch.value?.();
  }
}

function calculateOffsetTop() {
  const boundingClientRect = slotContent.value?.getBoundingClientRect();

  if (boundingClientRect) {
    return boundingClientRect.top + window.scrollY;
  }
}

watch(
  () => props.stickyOnScroll,
  () => initializePositionWatcher(),
);

onMounted(() => {
  nextTick(() => initializePositionWatcher());
});
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
.sp-toolbar {
  position: var(--position);
  background-color: var(--background-color, transparent);
  box-shadow: var(--box-shadow, none);

  &.--elevation-1 {
    --box-shadow: var(--sp-sys-elevation-bottom-1);
  }

  &.--elevation-2 {
    --box-shadow: var(--sp-sys-elevation-bottom-2);
  }

  &.--elevation-3 {
    --box-shadow: var(--sp-sys-elevation-bottom-3);
  }

  &.--color-white {
    --background-color: var(--sp-sys-color-surface);
  }

  &.--is-absolute {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
  }

  &.--is-sticky {
    position: fixed;
    top: var(--sp-comp-toolbar-sticky-top-position, v-bind(offsetInPx));
    left: 0;
    right: 0;
    z-index: var(--sp-comp-toolbar-sticky-z-index, 9999);
  }
}
</style>
